import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Bot,
  Check,
  ChevronLeft,
  ChevronRight,
  FileText,
  Image as ImageIcon,
  Loader2,
  Upload,
  X,
} from 'lucide-react';
import api from '../services/api.ts';
import { useTheme } from '../contexts/ThemeContext.tsx';
import AssistantIdentityForm from '../components/AssistantIdentityForm.tsx';
import TeamAndSpecialtiesForm from '../components/TeamAndSpecialtiesForm.tsx';
import SchedulingConfigForm from '../components/SchedulingConfigForm.tsx';
import FinancialConfigForm from '../components/FinancialConfigForm.tsx';
import './Register.css';

type JsonObject = Record<string, unknown>;
type SetupStep = 1 | 2 | 3 | 4 | 5;

interface DayConfig {
  open: boolean;
  morningEnabled: boolean;
  morningStart: string;
  morningEnd: string;
  afternoonEnabled: boolean;
  afternoonStart: string;
  afternoonEnd: string;
}

interface Treatment {
  treatmentTitle: string;
  description: string;
}

interface SetupGroups {
  assistant_identity: JsonObject;
  clinic_info: JsonObject;
  team_and_specialties: JsonObject;
  scheduling_config: JsonObject;
  financial_config: JsonObject;
  conversation_flow: JsonObject;
}

interface SetupValidation extends SetupGroups {
  status: string;
  updated_at?: string | null;
  version?: string | null;
  expires_at: string;
}

const EMPTY_DAY: DayConfig = {
  open: false,
  morningEnabled: false,
  morningStart: '',
  morningEnd: '',
  afternoonEnabled: false,
  afternoonStart: '',
  afternoonEnd: '',
};

const CONVERSATION_FLOW_ALLOWED_FIELDS = new Set<string>([
  ...Array.from({ length: 21 }, (_, index) => `step${index}`),
  'step1First',
  'step1Second',
  'max_tokens',
  'financial_redirect',
  'regular_redirect',
  'maintenance_redirect',
  'active_patients_redirect',
]);

const asObject = (value: unknown): JsonObject => (
  value && typeof value === 'object' && !Array.isArray(value)
    ? value as JsonObject
    : {}
);

const asString = (value: unknown): string => typeof value === 'string' ? value : '';
const pickAllowedFields = (value: unknown, allowed: ReadonlySet<string>): JsonObject => (
  Object.fromEntries(
    Object.entries(asObject(value)).filter(([key]) => allowed.has(key)),
  )
);
const asNumber = (value: unknown, fallback: number): number => (
  typeof value === 'number' && Number.isFinite(value) ? value : fallback
);
const asBoolean = (value: unknown): boolean => value === true;
const onlyDigits = (value: string): string => value.replace(/\D/g, '');

const getPhoneInfo = (value: string) => {
  const digits = onlyDigits(value);
  const hasBrazilDdi = digits.startsWith('55') && digits.length > 11;
  const nationalDigits = hasBrazilDdi ? digits.slice(2) : digits;
  return {
    digits,
    hasBrazilDdi,
    nationalDigits,
    isValid: [10, 11].includes(nationalDigits.length)
      && (!hasBrazilDdi || [12, 13].includes(digits.length)),
  };
};

const formatPhone = (value: string): string => {
  const { hasBrazilDdi, nationalDigits } = getPhoneInfo(value);
  const limitedNational = nationalDigits.substring(0, 11);
  const prefix = hasBrazilDdi ? '+55 ' : '';
  if (!limitedNational) return hasBrazilDdi ? '+55 ' : '';
  if (limitedNational.length <= 2) return `${prefix}(${limitedNational}`;
  const areaCode = limitedNational.slice(0, 2);
  const subscriber = limitedNational.slice(2);
  const firstPartSize = subscriber.length > 8 ? 5 : 4;
  const firstPart = subscriber.slice(0, firstPartSize);
  const secondPart = subscriber.slice(firstPartSize, firstPartSize + 4);
  return `${prefix}(${areaCode}) ${firstPart}${secondPart ? `-${secondPart}` : ''}`.trim();
};

const isValidPhone = (value: string): boolean => getPhoneInfo(value).isValid;

const isValidHttpUrl = (value: string): boolean => {
  try {
    const parsed = new URL(value.trim());
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
};

const fieldClass = 'w-full h-11 rounded-xl border border-gray-300 bg-white px-4 text-sm focus:ring-2 focus:ring-sminy-primary/50 outline-none';

const SetupField: React.FC<{
  id: string;
  label: string;
  children: React.ReactNode;
  required?: boolean;
}> = ({ id, label, children, required = true }) => (
  <div className="space-y-2">
    <label htmlFor={id} className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
      <span>{label}{required && <span className="text-red-500"> *</span>}</span>
    </label>
    {children}
  </div>
);

const TuringSetup: React.FC = () => {
  const { token = '' } = useParams<{ token: string }>();
  const { isDark } = useTheme();
  const [pageState, setPageState] = useState<'loading' | 'ready' | 'invalid' | 'error' | 'complete'>('loading');
  const [currentStep, setCurrentStep] = useState<SetupStep>(1);
  const [groups, setGroups] = useState<SetupGroups | null>(null);
  const [expectedUpdatedAt, setExpectedUpdatedAt] = useState<string | undefined>();
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const [clinicDisplayName, setClinicDisplayName] = useState('');
  const [adminPhone, setAdminPhone] = useState('');
  const [clinicLocation, setClinicLocation] = useState('');
  const [clinicAddress, setClinicAddress] = useState('');
  const [clinicWhatsApp, setClinicWhatsApp] = useState('');
  const [clinicLandline, setClinicLandline] = useState('');
  const [clinicInstagram, setClinicInstagram] = useState('');
  const [clinicFacebook, setClinicFacebook] = useState('');
  const [clinicSite, setClinicSite] = useState('');
  const [googleMapsLink, setGoogleMapsLink] = useState('');
  const [clinicHistory, setClinicHistory] = useState('');
  const [clinicLogo, setClinicLogo] = useState<string | null>(null);

  const [financialRedirectNumber, setFinancialRedirectNumber] = useState('');
  const [regularRedirectNumber, setRegularRedirectNumber] = useState('');
  const [maintenanceRedirectNumber, setMaintenanceRedirectNumber] = useState('');
  const [activePatientsRedirectNumber, setActivePatientsRedirectNumber] = useState('');

  const [assistantName, setAssistantName] = useState('');
  const [assistantRole, setAssistantRole] = useState('');
  const [assistantResponsibility, setAssistantResponsibility] = useState('Agendar consultas de avaliação');
  const [assistantFormality, setAssistantFormality] = useState('formal');
  const [assistantTone, setAssistantTone] = useState('amigável');
  const [assistantLanguage, setAssistantLanguage] = useState('pt-BR');

  const [technicalResponsible, setTechnicalResponsible] = useState('');
  const [treatments, setTreatments] = useState<Treatment[]>([]);

  const [monday, setMonday] = useState<DayConfig>(EMPTY_DAY);
  const [tuesday, setTuesday] = useState<DayConfig>(EMPTY_DAY);
  const [wednesday, setWednesday] = useState<DayConfig>(EMPTY_DAY);
  const [thursday, setThursday] = useState<DayConfig>(EMPTY_DAY);
  const [friday, setFriday] = useState<DayConfig>(EMPTY_DAY);
  const [saturday, setSaturday] = useState<DayConfig>(EMPTY_DAY);
  const [sunday, setSunday] = useState<DayConfig>(EMPTY_DAY);
  const [consultationDuration, setConsultationDuration] = useState(30);
  const [numberOfSuggestions, setNumberOfSuggestions] = useState(2);

  const [acceptsHealthInsurance, setAcceptsHealthInsurance] = useState(false);
  const [healthInsurancePlans, setHealthInsurancePlans] = useState('');
  const [paymentMethods, setPaymentMethods] = useState('');
  const [installmentConditions, setInstallmentConditions] = useState('');
  const [evaluationPrice, setEvaluationPrice] = useState('');
  const [treatmentPrices, setTreatmentPrices] = useState('');
  const [pricesUnderEvaluation, setPricesUnderEvaluation] = useState(false);

  useEffect(() => {
    const currentMeta = document.head.querySelector<HTMLMetaElement>('meta[name="referrer"]');
    const createdMeta = currentMeta === null;
    const meta = currentMeta ?? document.createElement('meta');
    const originalContent = meta.content;
    if (createdMeta) {
      meta.name = 'referrer';
      document.head.appendChild(meta);
    }
    meta.content = 'no-referrer';
    return () => {
      if (createdMeta) meta.remove();
      else meta.content = originalContent;
    };
  }, []);

  useEffect(() => {
    if (!token) {
      setPageState('invalid');
      return;
    }
    const controller = new AbortController();
    let active = true;
    setPageState('loading');
    api.post<SetupValidation>('/api/public/turing-setup/validate', { token }, { signal: controller.signal })
      .then(({ data }) => {
        if (!active) return;
        const clinic = asObject(data.clinic_info);
        const assistant = asObject(data.assistant_identity);
        const team = asObject(data.team_and_specialties);
        const schedule = asObject(data.scheduling_config);
        const financial = asObject(data.financial_config);
        const flow = asObject(data.conversation_flow);

        const redirect = (key: string) => asObject(flow[key]);
        setGroups({
          assistant_identity: assistant,
          clinic_info: clinic,
          team_and_specialties: team,
          scheduling_config: schedule,
          financial_config: financial,
          conversation_flow: flow,
        });
        setExpectedUpdatedAt(data.updated_at || data.version || undefined);

        setClinicDisplayName(asString(clinic.clinic_name));
        setAdminPhone(formatPhone(asString(clinic.admin_phone)));
        setClinicLocation(asString(clinic.clinic_location));
        setClinicAddress(asString(clinic.clinic_address));
        setClinicWhatsApp(formatPhone(asString(clinic.clinic_whatsapp)));
        setClinicLandline(formatPhone(asString(clinic.clinic_landline || clinic.clinic_phone_fixed)));
        setClinicInstagram(asString(clinic.clinic_instagram));
        setClinicFacebook(asString(clinic.clinic_facebook));
        setClinicSite(asString(clinic.clinic_site));
        setGoogleMapsLink(asString(clinic.google_maps_link || clinic.clinic_maps));
        setClinicHistory(asString(clinic.clinic_history));
        setClinicLogo(typeof clinic.clinic_logo === 'string' ? clinic.clinic_logo : null);

        setFinancialRedirectNumber(formatPhone(asString(redirect('financial_redirect').number)));
        setRegularRedirectNumber(formatPhone(asString(redirect('regular_redirect').number)));
        setMaintenanceRedirectNumber(formatPhone(asString(redirect('maintenance_redirect').number)));
        setActivePatientsRedirectNumber(formatPhone(asString(redirect('active_patients_redirect').number)));

        setAssistantName(asString(assistant.assistant_name));
        setAssistantRole(asString(assistant.assistant_role));
        setAssistantResponsibility(asString(assistant.assistant_responsibility) || 'Agendar consultas de avaliação');
        setAssistantFormality(asString(assistant.assistant_formality) || 'formal');
        setAssistantTone(asString(assistant.assistant_tone) || 'amigável');
        setAssistantLanguage(asString(assistant.assistant_language) || 'pt-BR');

        const rawTreatments = Array.isArray(team.treatments) ? team.treatments : [];
        setTechnicalResponsible(asString(team.technical_responsible));
        setTreatments(rawTreatments.map((value) => {
          const treatment = asObject(value);
          return {
            treatmentTitle: asString(treatment.treatmentTitle),
            description: asString(treatment.description),
          };
        }));

        const day = (key: string): DayConfig => {
          const value = asObject(schedule[key]);
          return {
            ...EMPTY_DAY,
            open: asBoolean(value.open),
            morningEnabled: asBoolean(value.morningEnabled),
            morningStart: asString(value.morningStart),
            morningEnd: asString(value.morningEnd),
            afternoonEnabled: asBoolean(value.afternoonEnabled),
            afternoonStart: asString(value.afternoonStart),
            afternoonEnd: asString(value.afternoonEnd),
          };
        };
        setMonday(day('monday'));
        setTuesday(day('tuesday'));
        setWednesday(day('wednesday'));
        setThursday(day('thursday'));
        setFriday(day('friday'));
        setSaturday(day('saturday'));
        setSunday(day('sunday'));
        setConsultationDuration(asNumber(schedule.consultation_duration, 30));
        setNumberOfSuggestions(asNumber(schedule.number_of_suggestions, 2));

        setAcceptsHealthInsurance(asBoolean(financial.accepts_health_insurance));
        setHealthInsurancePlans(asString(financial.health_insurance_plans));
        const paymentMethodsValue = financial.payment_methods;
        setPaymentMethods(Array.isArray(paymentMethodsValue)
          ? paymentMethodsValue.filter((item): item is string => typeof item === 'string').join(', ')
          : asString(paymentMethodsValue));
        setInstallmentConditions(asString(financial.installment_conditions));
        setEvaluationPrice(asString(financial.evaluation_price));
        setTreatmentPrices(asString(financial.treatment_prices));
        setPricesUnderEvaluation(asBoolean(financial.prices_under_evaluation));
        setPageState('ready');
      })
      .catch((requestError: unknown) => {
        if (!active || controller.signal.aborted) return;
        const responseError = requestError as {
          response?: { status?: number; data?: { detail?: string | { code?: string } } };
        };
        const detail = responseError.response?.data?.detail;
        if (
          responseError.response?.status === 410
          && typeof detail === 'object'
          && detail?.code === 'expired'
        ) {
          setError('Este link expirou. Peça um novo link para concluir a configuração.');
          setPageState('error');
          return;
        }
        if (responseError.response?.status === 404 || responseError.response?.status === 410) {
          setPageState('invalid');
          return;
        }
        setError(typeof detail === 'string' ? detail : 'Não foi possível carregar a configuração. Tente novamente.');
        setPageState('error');
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [token]);

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      setError('O logo deve ter no máximo 2MB.');
      event.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setClinicLogo(typeof reader.result === 'string' ? reader.result : null);
      setError('');
    };
    reader.readAsDataURL(file);
    event.target.value = '';
  };

  const validateClinic = (): boolean => {
    if (!clinicDisplayName.trim()) { setError('Por favor, insira o nome fantasia da clínica'); return false; }
    if (!isValidPhone(adminPhone)) { setError('Por favor, insira um WhatsApp válido para o administrador'); return false; }
    if (!isValidPhone(clinicWhatsApp)) { setError('Por favor, insira um WhatsApp de suporte válido'); return false; }
    if (!clinicLocation.trim()) { setError('Por favor, insira a cidade/estado da clínica'); return false; }
    if (!clinicAddress.trim()) { setError('Por favor, insira o endereço completo da clínica'); return false; }
    if (!isValidPhone(clinicLandline)) { setError('Por favor, insira um telefone fixo válido'); return false; }
    if (!isValidPhone(financialRedirectNumber)) { setError('Por favor, insira um telefone válido para redirecionamento financeiro'); return false; }
    if (!isValidPhone(regularRedirectNumber)) { setError('Por favor, insira um telefone válido para redirecionamento geral'); return false; }
    if (!isValidPhone(maintenanceRedirectNumber)) { setError('Por favor, insira um telefone válido para redirecionamento de manutenção'); return false; }
    if (!isValidPhone(activePatientsRedirectNumber)) { setError('Por favor, insira um telefone válido para redirecionamento de pacientes ativos'); return false; }
    if (!isValidHttpUrl(googleMapsLink)) { setError('Por favor, insira um link válido do Google Maps começando com http:// ou https://'); return false; }
    if (!isValidHttpUrl(clinicInstagram)) { setError('Por favor, insira uma URL válida do Instagram'); return false; }
    if (!isValidHttpUrl(clinicFacebook)) { setError('Por favor, insira uma URL válida do Facebook'); return false; }
    if (!isValidHttpUrl(clinicSite)) { setError('Por favor, insira uma URL válida do site'); return false; }
    if (!clinicHistory.trim()) { setError('Por favor, conte um pouco sobre a história da clínica'); return false; }
    return true;
  };

  const validateAssistant = (): boolean => {
    if (!assistantName.trim()) { setError('Por favor, insira o nome do assistente'); return false; }
    if (!assistantRole.trim()) { setError('Por favor, insira a função/cargo do assistente'); return false; }
    return true;
  };

  const validateTeam = (): boolean => {
    if (!technicalResponsible.trim()) { setError('Por favor, insira o responsável técnico da clínica'); return false; }
    if (treatments.length === 0) { setError('Adicione pelo menos um tratamento oferecido'); return false; }
    const invalidIndex = treatments.findIndex((treatment) => (
      !treatment.treatmentTitle.trim() || !treatment.description.trim()
    ));
    if (invalidIndex >= 0) {
      setError(`Preencha o nome e a descrição do tratamento #${invalidIndex + 1}`);
      return false;
    }
    return true;
  };

  const validateSchedule = (): boolean => {
    const days = [
      ['Segunda', monday], ['Terça', tuesday], ['Quarta', wednesday],
      ['Quinta', thursday], ['Sexta', friday], ['Sábado', saturday], ['Domingo', sunday],
    ] as const;
    if (!days.some(([, day]) => day.morningEnabled || day.afternoonEnabled)) {
      setError('Configure pelo menos um período de atendimento na semana');
      return false;
    }
    for (const [label, day] of days) {
      for (const [period, enabled, start, end] of [
        ['manhã', day.morningEnabled, day.morningStart, day.morningEnd],
        ['tarde', day.afternoonEnabled, day.afternoonStart, day.afternoonEnd],
      ] as const) {
        if (enabled && (!start || !end)) {
          setError(`Informe início e fim para ${period} de ${label}`);
          return false;
        }
        if (enabled && start >= end) {
          setError(`O horário final deve ser maior que o inicial em ${period} de ${label}`);
          return false;
        }
      }
    }
    if (!consultationDuration || consultationDuration < 1) {
      setError('Informe uma duração válida para as consultas');
      return false;
    }
    return true;
  };

  const validateFinancial = (): boolean => {
    if (acceptsHealthInsurance && !healthInsurancePlans.trim()) { setError('Informe os convênios aceitos ou desmarque a opção de convênios'); return false; }
    if (!paymentMethods.trim()) { setError('Informe as formas de pagamento'); return false; }
    if (!installmentConditions.trim()) { setError('Informe as condições de parcelamento'); return false; }
    if (!evaluationPrice.trim()) { setError('Informe o preço da avaliação ou marque avaliação gratuita'); return false; }
    if (!pricesUnderEvaluation && !treatmentPrices.trim()) { setError('Informe a tabela de preços dos tratamentos ou marque valores somente após avaliação'); return false; }
    return true;
  };

  const validators = [validateClinic, validateAssistant, validateTeam, validateSchedule, validateFinancial];
  const validateCurrentStep = (): boolean => validators[currentStep - 1]();

  const handleNext = () => {
    setError('');
    if (!validateCurrentStep()) {
      window.scrollTo(0, 0);
      return;
    }
    window.scrollTo(0, 0);
    if (currentStep < 5) setCurrentStep((step) => (step + 1) as SetupStep);
  };

  const handleBack = () => {
    setError('');
    window.scrollTo(0, 0);
    if (currentStep > 1) setCurrentStep((step) => (step - 1) as SetupStep);
  };

  const handleComplete = async () => {
    if (!groups) return;
    setIsLoading(true);
    setError('');
    try {
      for (let index = 0; index < validators.length; index += 1) {
        if (!validators[index]()) {
          setCurrentStep((index + 1) as SetupStep);
          setIsLoading(false);
          window.scrollTo(0, 0);
          return;
        }
      }

      const previousFlow = groups.conversation_flow;
      const redirect = (key: string, number: string) => {
        const current = asObject(previousFlow[key]);
        return {
          type: asString(current.type) || 'fixo',
          number: onlyDigits(number),
        };
      };
      const conversationFlow = pickAllowedFields(
        previousFlow,
        CONVERSATION_FLOW_ALLOWED_FIELDS,
      );
      const requestPayload = {
        token,
        ...(expectedUpdatedAt ? { expected_updated_at: expectedUpdatedAt } : {}),
        assistant_identity: {
          assistant_name: assistantName,
          assistant_role: assistantRole,
          assistant_responsibility: assistantResponsibility,
          assistant_formality: assistantFormality,
          assistant_tone: assistantTone,
          assistant_language: assistantLanguage,
        },
        clinic_info: {
          clinic_name: clinicDisplayName,
          clinic_location: clinicLocation,
          clinic_address: clinicAddress,
          clinic_landline: onlyDigits(clinicLandline),
          clinic_phone_fixed: onlyDigits(clinicLandline),
          clinic_whatsapp: onlyDigits(clinicWhatsApp),
          admin_phone: onlyDigits(adminPhone),
          clinic_instagram: clinicInstagram,
          clinic_facebook: clinicFacebook,
          clinic_site: clinicSite,
          google_maps_link: googleMapsLink,
          clinic_maps: googleMapsLink,
          clinic_history: clinicHistory,
          clinic_logo: clinicLogo,
        },
        team_and_specialties: {
          technical_responsible: technicalResponsible,
          treatments,
        },
        scheduling_config: {
          monday, tuesday, wednesday, thursday, friday, saturday, sunday,
          consultation_duration: consultationDuration,
          number_of_suggestions: numberOfSuggestions,
        },
        financial_config: {
          accepts_health_insurance: acceptsHealthInsurance,
          health_insurance_plans: healthInsurancePlans,
          payment_methods: paymentMethods.split(',').map((value) => value.trim()).filter(Boolean),
          installment_conditions: installmentConditions,
          evaluation_price: evaluationPrice,
          treatment_prices: treatmentPrices,
          prices_under_evaluation: pricesUnderEvaluation,
        },
        conversation_flow: {
          ...conversationFlow,
          financial_redirect: redirect('financial_redirect', financialRedirectNumber),
          regular_redirect: redirect('regular_redirect', regularRedirectNumber),
          maintenance_redirect: redirect('maintenance_redirect', maintenanceRedirectNumber),
          active_patients_redirect: redirect('active_patients_redirect', activePatientsRedirectNumber),
        },
      };

      await api.post('/api/public/turing-setup/complete', requestPayload);
      setPageState('complete');
    } catch (requestError: unknown) {
      const responseError = requestError as {
        response?: { status?: number; data?: { detail?: string } };
      };
      if (responseError.response?.status === 404 || responseError.response?.status === 410) {
        setPageState('invalid');
        return;
      }
      setError(responseError.response?.data?.detail || 'Não foi possível concluir agora. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const steps = ['Clínica', 'Assistente', 'Equipe', 'Agendamento', 'Financeiro'];
  const titles: Record<SetupStep, string> = {
    1: 'Clínica',
    2: 'Assistente',
    3: 'Equipe e especialidades',
    4: 'Agendamento',
    5: 'Financeiro',
  };
  const descriptions: Record<SetupStep, string> = {
    1: 'Revise os dados que serão usados para preparar o atendimento.',
    2: 'Defina como seu assistente se apresenta e conversa.',
    3: 'Apresente os profissionais e tratamentos da clínica.',
    4: 'Escolha os dias e horários disponíveis para avaliações.',
    5: 'Informe as condições que o assistente poderá apresentar.',
  };
  const stepIndicator = (
    <nav aria-label="Etapas da configuração" className="register-progress" style={{ '--register-step-index': String(currentStep), '--register-step-count': '5' } as React.CSSProperties}>
      <p className="register-mobile-progress">Etapa {currentStep} de 5 · {steps[currentStep - 1]}</p>
      <ol>{steps.map((label, index) => (
        <li key={label} aria-current={currentStep === index + 1 ? 'step' : undefined} className={currentStep > index + 1 ? 'is-complete' : ''}>
          <span className="register-step-number" aria-hidden="true">{currentStep > index + 1 ? <Check size={16} /> : index + 1}</span>
          <span className="register-step-label">{label}</span>
        </li>
      ))}</ol>
    </nav>
  );

  const header = (
    <header className="register-header">
      <a href="/" aria-label="Sminy.ai — início" className="register-brand">Sminy.ai</a>
      <p>Configuração da clínica</p>
    </header>
  );

  if (pageState === 'loading') {
    return (
      <div data-theme={isDark ? 'dark' : 'light'} className="register-page min-h-screen w-full">
        {header}
        <main className="register-card mx-auto flex w-full max-w-lg items-center justify-center gap-3" role="status">
          <Loader2 className="h-5 w-5 animate-spin text-sminy-primary" />
          <span>Carregando a configuração…</span>
        </main>
      </div>
    );
  }

  if (pageState === 'invalid') {
    return (
      <div data-theme={isDark ? 'dark' : 'light'} className="register-page min-h-screen w-full">
        {header}
        <main className="register-card mx-auto w-full max-w-lg text-center">
          <h1 className="mb-2 text-2xl font-semibold">Link expirado ou indisponível</h1>
          <p className="text-sm text-gray-600">Solicite um novo link de configuração à equipe da Turing Academy.</p>
        </main>
      </div>
    );
  }

  if (pageState === 'complete') {
    return (
      <div data-theme={isDark ? 'dark' : 'light'} className="register-page min-h-screen w-full flex items-center justify-center">
        <main className="register-card w-full max-w-lg p-6 text-center md:p-10">
          <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-green-100">
            <Check className="h-10 w-10 text-green-600" />
          </div>
          <h1 className="mb-2 text-2xl font-bold">Configuração concluída</h1>
          <p className="text-sm text-gray-600">Os dados da clínica foram salvos com sucesso.</p>
        </main>
      </div>
    );
  }

  if (pageState === 'error') {
    return (
      <div data-theme={isDark ? 'dark' : 'light'} className="register-page min-h-screen w-full">
        {header}
        <main className="register-card mx-auto w-full max-w-lg text-center">
          <h1 className="mb-2 text-2xl font-semibold">Não foi possível carregar</h1>
          <p role="alert" className="mb-5 text-sm text-gray-600">{error}</p>
          <button type="button" onClick={() => window.location.reload()} className="h-11 rounded-xl bg-sminy-primary px-5 text-sm font-medium text-white hover:bg-sminy-primary-hover">
            Tentar novamente
          </button>
        </main>
      </div>
    );
  }

  return (
    <div data-theme={isDark ? 'dark' : 'light'} className="register-page">
      {header}
      <div className="register-layout">
        <aside className="register-sidebar">
          <div className="register-sidebar-intro"><h2>Sua clínica na Sminy</h2><p>Um passo de cada vez para preparar o seu atendimento.</p></div>
          {stepIndicator}
          <p className="register-sidebar-note">Revise os dados para configurar um atendimento com a identidade da sua clínica.</p>
        </aside>
        <main className="register-card">
          <div className="register-title" aria-live="polite">
            <h1>{titles[currentStep]}</h1>
            <p>{descriptions[currentStep]}</p>
          </div>
          {error && <div role="alert" className="mb-4 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-600">{error}</div>}
          <form aria-label={titles[currentStep]} onSubmit={(event) => event.preventDefault()}>
            {currentStep === 1 && (
              <div className="space-y-4">
                <h2 className="register-section-title">Identificação da clínica</h2>
                <div className="space-y-2">
                  <SetupField id="turing-setup-logo" label="Logo da Clínica" required={false}>
                    <div className="flex items-center gap-4">
                      {clinicLogo ? (
                        <div className="relative h-20 w-20 overflow-hidden rounded-xl border border-gray-200">
                          <img src={clinicLogo} alt="Logo da clínica" className="h-full w-full object-cover" />
                          <button type="button" onClick={() => setClinicLogo(null)} aria-label="Remover logo" className="absolute right-1 top-1 rounded-full bg-red-500 p-1 text-white"><X className="h-3 w-3" /></button>
                        </div>
                      ) : (
                        <div className="flex h-20 w-20 items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-100 text-gray-400"><ImageIcon className="h-8 w-8" /></div>
                      )}
                      <label htmlFor="turing-setup-logo" className="inline-flex cursor-pointer items-center gap-2 rounded-xl bg-sminy-primary/10 px-4 py-2 text-sm font-medium text-sminy-primary transition-colors hover:bg-sminy-primary/20">
                        <Upload className="h-4 w-4" /> Carregar logo
                      </label>
                      <input id="turing-setup-logo" type="file" accept="image/*" className="sr-only" onChange={handleLogoUpload} />
                    </div>
                  </SetupField>
                </div>
                <SetupField id="turing-clinic-name" label="Nome Fantasia">
                  <input id="turing-clinic-name" required value={clinicDisplayName} onChange={(event) => setClinicDisplayName(event.target.value)} className={fieldClass} placeholder="Nome para pacientes" />
                </SetupField>
                <SetupField id="turing-admin-phone" label="WhatsApp do Administrador">
                  <input id="turing-admin-phone" type="tel" required inputMode="tel" autoComplete="tel" value={adminPhone} onChange={(event) => setAdminPhone(formatPhone(event.target.value))} className={fieldClass} placeholder="(99) 99999-9999" maxLength={19} />
                </SetupField>
                <SetupField id="turing-human-whatsapp" label="WhatsApp de Suporte Humano">
                  <input id="turing-human-whatsapp" type="tel" required inputMode="tel" autoComplete="tel" value={clinicWhatsApp} onChange={(event) => setClinicWhatsApp(formatPhone(event.target.value))} className={fieldClass} placeholder="(99) 99999-9999" maxLength={19} />
                  <div className="flex gap-2 rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-xs text-yellow-700"><Bot className="h-4 w-4 shrink-0" /><p><strong>Atenção:</strong> Este é o número para onde o paciente será direcionado caso precise falar com um atendente humano.</p></div>
                </SetupField>
                <h2 className="register-section-title">Localização</h2>
                <SetupField id="turing-clinic-location" label="Cidade/Estado">
                  <input id="turing-clinic-location" required value={clinicLocation} onChange={(event) => setClinicLocation(event.target.value)} className={fieldClass} placeholder="São Paulo, SP" />
                </SetupField>
                <SetupField id="turing-clinic-address" label="Endereço Completo">
                  <input id="turing-clinic-address" required value={clinicAddress} onChange={(event) => setClinicAddress(event.target.value)} className={fieldClass} />
                </SetupField>
                <SetupField id="turing-clinic-landline" label="Telefone Fixo">
                  <input id="turing-clinic-landline" type="tel" required inputMode="tel" value={clinicLandline} onChange={(event) => setClinicLandline(formatPhone(event.target.value))} className={fieldClass} placeholder="(99) 9999-9999" maxLength={19} />
                </SetupField>
                <div className="border-t border-gray-100 pb-2 pt-4">
                  <h3 className="mb-4 text-sm font-semibold text-gray-900">Contatos para Redirecionamento</h3>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <SetupField id="turing-financial-redirect" label="Financeiro"><input id="turing-financial-redirect" type="tel" required inputMode="tel" value={financialRedirectNumber} onChange={(event) => setFinancialRedirectNumber(formatPhone(event.target.value))} className={fieldClass} placeholder="(99) 99999-9999" maxLength={19} /></SetupField>
                    <SetupField id="turing-regular-redirect" label="Regular (Geral)"><input id="turing-regular-redirect" type="tel" required inputMode="tel" value={regularRedirectNumber} onChange={(event) => setRegularRedirectNumber(formatPhone(event.target.value))} className={fieldClass} placeholder="(99) 99999-9999" maxLength={19} /></SetupField>
                    <SetupField id="turing-maintenance-redirect" label="Manutenção"><input id="turing-maintenance-redirect" type="tel" required inputMode="tel" value={maintenanceRedirectNumber} onChange={(event) => setMaintenanceRedirectNumber(formatPhone(event.target.value))} className={fieldClass} placeholder="(99) 99999-9999" maxLength={19} /></SetupField>
                    <SetupField id="turing-active-redirect" label="Pacientes Ativos"><input id="turing-active-redirect" type="tel" required inputMode="tel" value={activePatientsRedirectNumber} onChange={(event) => setActivePatientsRedirectNumber(formatPhone(event.target.value))} className={fieldClass} placeholder="(99) 99999-9999" maxLength={19} /></SetupField>
                  </div>
                  <p className="mt-3 text-xs text-gray-400">Todos os redirecionamentos são obrigatórios para evitar encaminhamento errado no atendimento.</p>
                </div>
                <div className="space-y-4 pt-2">
                  <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900"><FileText className="h-4 w-4 text-sminy-primary" /> Presença Digital</h3>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <SetupField id="turing-google-maps" label="Link do Google Maps"><input id="turing-google-maps" type="url" required value={googleMapsLink} onChange={(event) => setGoogleMapsLink(event.target.value)} className={fieldClass} placeholder="https://maps.app.goo.gl/..." /></SetupField>
                    <SetupField id="turing-instagram" label="Instagram (URL)"><input id="turing-instagram" type="url" required value={clinicInstagram} onChange={(event) => setClinicInstagram(event.target.value)} className={fieldClass} placeholder="https://instagram.com/..." /></SetupField>
                    <SetupField id="turing-facebook" label="Facebook (URL)"><input id="turing-facebook" type="url" required value={clinicFacebook} onChange={(event) => setClinicFacebook(event.target.value)} className={fieldClass} placeholder="https://facebook.com/..." /></SetupField>
                    <SetupField id="turing-site" label="Site"><input id="turing-site" type="url" required value={clinicSite} onChange={(event) => setClinicSite(event.target.value)} className={fieldClass} placeholder="https://site.com.br" /></SetupField>
                  </div>
                </div>
                <SetupField id="turing-clinic-history" label="História da clínica">
                  <textarea id="turing-clinic-history" value={clinicHistory} onChange={(event) => setClinicHistory(event.target.value)} rows={3} required className="w-full resize-none rounded-xl border border-gray-300 p-4 text-sm focus:ring-2 focus:ring-sminy-primary/50 outline-none" placeholder="Conte um pouco sobre a história da sua clínica..." />
                </SetupField>
              </div>
            )}

            {currentStep === 2 && (
              <AssistantIdentityForm
                persistOnChange
                assistantName={assistantName}
                setAssistantName={setAssistantName}
                assistantRole={assistantRole}
                setAssistantRole={setAssistantRole}
                assistantResponsibility={assistantResponsibility}
                setAssistantResponsibility={setAssistantResponsibility}
                assistantFormality={assistantFormality}
                setAssistantFormality={setAssistantFormality}
                assistantTone={assistantTone}
                setAssistantTone={setAssistantTone}
                assistantLanguage={assistantLanguage}
                setAssistantLanguage={setAssistantLanguage}
                disabledResponsibility
              />
            )}

            {currentStep === 3 && (
              <TeamAndSpecialtiesForm
                persistOnChange
                technicalResponsible={technicalResponsible}
                setTechnicalResponsible={setTechnicalResponsible}
                treatments={treatments}
                setTreatments={setTreatments}
              />
            )}

            {currentStep === 4 && (
              <SchedulingConfigForm
                persistOnChange
                monday={monday} setMonday={setMonday}
                tuesday={tuesday} setTuesday={setTuesday}
                wednesday={wednesday} setWednesday={setWednesday}
                thursday={thursday} setThursday={setThursday}
                friday={friday} setFriday={setFriday}
                saturday={saturday} setSaturday={setSaturday}
                sunday={sunday} setSunday={setSunday}
                consultationDuration={consultationDuration}
                setConsultationDuration={setConsultationDuration}
                numberOfSuggestions={numberOfSuggestions}
                setNumberOfSuggestions={setNumberOfSuggestions}
                disabledSuggestions
              />
            )}

            {currentStep === 5 && (
              <FinancialConfigForm
                persistOnChange
                acceptsHealthInsurance={acceptsHealthInsurance}
                setAcceptsHealthInsurance={setAcceptsHealthInsurance}
                healthInsurancePlans={healthInsurancePlans}
                setHealthInsurancePlans={setHealthInsurancePlans}
                paymentMethods={paymentMethods}
                setPaymentMethods={setPaymentMethods}
                installmentConditions={installmentConditions}
                setInstallmentConditions={setInstallmentConditions}
                evaluationPrice={evaluationPrice}
                setEvaluationPrice={setEvaluationPrice}
                treatmentPrices={treatmentPrices}
                setTreatmentPrices={setTreatmentPrices}
                pricesUnderEvaluation={pricesUnderEvaluation}
                setPricesUnderEvaluation={setPricesUnderEvaluation}
              />
            )}

            <div className="register-actions mt-8 flex gap-3">
              {currentStep > 1 && (
                <button type="button" onClick={handleBack} disabled={isLoading} className="flex h-11 items-center justify-center gap-2 rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                  <ChevronLeft className="h-4 w-4" /> Voltar
                </button>
              )}
              {currentStep < 5 ? (
                <button type="button" onClick={handleNext} disabled={isLoading} className="flex h-11 items-center justify-center gap-2 rounded-xl bg-sminy-primary px-4 py-2 text-sm font-medium text-white hover:bg-sminy-primary-hover">
                  Continuar <ChevronRight className="h-4 w-4" />
                </button>
              ) : (
                <button type="button" onClick={handleComplete} disabled={isLoading} className="flex h-11 items-center justify-center gap-2 rounded-xl bg-sminy-primary px-4 py-2 text-sm font-medium text-white transition-all hover:bg-sminy-primary-hover disabled:cursor-not-allowed disabled:bg-gray-400">
                  {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                  {isLoading ? 'Salvando configuração…' : 'Concluir configuração'}
                </button>
              )}
            </div>
          </form>
        </main>
      </div>
    </div>
  );
};

export default TuringSetup;
